import { Button, cn, HorizontalMenu } from "@win-reality/react-ui";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import { ReactNode, useCallback, useEffect, useState } from "react";

import { TeamSide } from "../../constants/teams";
import { GameFullResponse, LineUpPlayer } from "../../types/gameFull";
import { GameRosters } from "../../types/roster";
import { isLineupDraftChanged } from "../../utils/lineupDiff";
import {
  addToBattingOrder,
  createLineupDraft,
  getBench,
  LineupDraft,
  removeFromBattingOrder,
  reorderBattingOrder,
} from "../../utils/lineupDraft";
import { TeamDisplays } from "../../utils/teamColors";
import { AddPlayersDialog } from "./AddPlayersDialog";
import { DiscardChangesDialog } from "./DiscardChangesDialog";
import { LineupFooter } from "./LineupFooter";
import { AddablePlayerRow, BattingOrderList } from "./PlayerRows";

const SIDES: { side: TeamSide; label: string }[] = [
  { side: "away", label: "Away Lineup" },
  { side: "home", label: "Home Lineup" },
];

const Section = ({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) => (
  <section className="space-y-3">
    <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
      {title}
    </h4>
    {children}
  </section>
);

type PanelContentProps = {
  data: GameFullResponse;
  rosters: GameRosters;
  teamDisplays: TeamDisplays;
  /** Saves the edited lineups; the parent closes the panel when they save */
  onSave: (draft: LineupDraft) => Promise<void>;
  /** Closes without saving */
  onClose: () => void;
};

/** Mounted per open so the draft starts from the current lineups. */
const PanelContent = ({
  data,
  rosters,
  teamDisplays,
  onSave,
  onClose,
}: PanelContentProps) => {
  const [side, setSide] = useState<TeamSide>(
    data.game.isTopOfInning ? "away" : "home",
  );
  const [draft, setDraft] = useState(() => createLineupDraft(data, rosters));
  const [isAddPlayersOpen, setIsAddPlayersOpen] = useState(false);
  const [isDiscardOpen, setIsDiscardOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const isChanged = isLineupDraftChanged(data.lineUp, draft);

  const team = draft[side];
  const bench = getBench(team);
  const teamName = data.game.teams[side].name;
  const parentName = rosters[side].parentName ?? teamName;
  // Only one hitter across both lineups is active: the live game's hitter
  const activePlayerId = data.game.currentHitter?.playerId;

  const save = async () => {
    setIsSaving(true);
    try {
      await onSave(draft);
    } finally {
      setIsSaving(false);
    }
  };

  // Unsaved edits ask before they're dropped
  const requestClose = useCallback(() => {
    if (isChanged) setIsDiscardOpen(true);
    else onClose();
  }, [isChanged, onClose]);

  // Escape closes like the X, unless a dialog of its own is open
  useEffect(() => {
    if (isAddPlayersOpen || isDiscardOpen || isSaving) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") requestClose();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [requestClose, isAddPlayersOpen, isDiscardOpen, isSaving]);

  const addPlayers = (players: LineUpPlayer[]) =>
    setDraft((prev) => addToBattingOrder(prev, side, players));

  return (
    <>
      {/* Header */}
      <div className="flex items-center justify-between px-6 pb-2 pt-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
          Manage Lineups
        </h2>
        <Button
          variant="secondary-flat-gray"
          size="icon-md"
          onClick={requestClose}
          isDisabled={isSaving}
          aria-label="Close"
          data-testid="lineup-close-button"
          className={cn(
            // Border
            "rounded-full",
            // Background: light gray in light mode; dark keeps the variant's
            "bg-gray-200 hover:bg-gray-300",
            "dark:bg-accent-light-4 dark:hover:bg-accent-light-4/80",
            // Text
            "text-gray-700 dark:text-accent-dark-1",
          )}
        >
          <X className="h-5 w-5" />
        </Button>
      </div>

      <div className="px-6">
        <HorizontalMenu
          size="md"
          items={SIDES.map((s) => ({
            label: s.label,
            active: s.side === side,
            onClick: () => setSide(s.side),
          }))}
          wrapperClassName="flex justify-center"
          buttonClassName="text-gray-900 dark:text-white"
        />
      </div>

      <div className="tagging-scrollbar flex-1 space-y-6 overflow-y-auto px-6 py-4">
        {/* (1) Roster and team name */}
        <div>
          <div className="text-lg font-bold text-gray-900 dark:text-white">
            {team.rosterName}
          </div>
          <div className="text-xs text-gray-900 dark:text-white">
            {parentName}
          </div>
        </div>

        {/* (2) Batting order, hidden when empty */}
        {team.battingOrder.length > 0 && (
          <Section title="Batting Order">
            <BattingOrderList
              data-team={side}
              players={team.battingOrder}
              teamColor={teamDisplays[side].color}
              teamTextColor={teamDisplays[side].textColor}
              activePlayerId={activePlayerId}
              onReorder={(players) =>
                setDraft((prev) => reorderBattingOrder(prev, side, players))
              }
              onRemove={(player) =>
                setDraft((prev) =>
                  removeFromBattingOrder(prev, side, player.playerId),
                )
              }
            />
          </Section>
        )}

        {/* (3) Bench: roster minus batting order */}
        <Section title={`${teamName} Bench`}>
          {bench.length === 0 ? (
            <div
              data-testid="lineup-bench-empty-status"
              className="text-sm text-tagging-muted"
            >
              Empty
            </div>
          ) : (
            <div
              data-testid="lineup-bench-list"
              data-team={side}
              className="space-y-2"
            >
              {bench.map((player) => (
                <AddablePlayerRow
                  key={player.playerId}
                  player={player}
                  onAdd={() => addPlayers([player])}
                  data-testid="lineup-bench-item"
                />
              ))}
            </div>
          )}
        </Section>

        {/* (4) Players of interest not on a roster, hidden when empty */}
        {draft.otherPlayers.length > 0 && (
          <Section title="Other Players at this Game">
            <div
              data-testid="lineup-other-player-list"
              data-team={side}
              className="space-y-2"
            >
              {draft.otherPlayers.map((player) => (
                <AddablePlayerRow
                  key={player.playerId}
                  player={player}
                  onAdd={() => addPlayers([player])}
                  // They join the roster too
                  roster={rosters[side]}
                  data-testid="lineup-other-player-item"
                />
              ))}
            </div>
          </Section>
        )}
      </div>

      <LineupFooter
        roster={rosters[side]}
        isChanged={isChanged}
        isSaving={isSaving}
        onAddPlayers={() => setIsAddPlayersOpen(true)}
        onSave={() => void save()}
      />

      <DiscardChangesDialog
        isOpen={isDiscardOpen}
        onKeepEditing={() => setIsDiscardOpen(false)}
        onDiscard={onClose}
      />

      {isAddPlayersOpen && (
        <AddPlayersDialog
          rosterPlayerIds={new Set(team.roster.map((p) => p.playerId))}
          orgId={rosters[side].orgId}
          onClose={() => setIsAddPlayersOpen(false)}
          onAddPlayers={addPlayers}
        />
      )}
    </>
  );
};

type LineupPanelProps = {
  isOpen: boolean;
  data: GameFullResponse;
  rosters: GameRosters;
  teamDisplays: TeamDisplays;
  /** Saves the edited lineups; the parent closes the panel when they save */
  onSave: (draft: LineupDraft) => Promise<void>;
  /** Closes without saving (after the discard confirmation, if edited) */
  onClose: () => void;
};

/**
 * Lineup management side panel (spec 1.5): slides over the sidebar from the
 * left with the rest of the page dimmed. Edits save with Save Lineup.
 */
export const LineupPanel = ({
  isOpen,
  data,
  rosters,
  teamDisplays,
  onSave,
  onClose,
}: LineupPanelProps) => (
  <AnimatePresence>
    {isOpen && (
      <div
        key="lineup-management"
        // Marks a modal surface so the pitch tagging hotkeys stay quiet
        role="dialog"
        aria-modal="true"
        aria-label="Manage Lineups"
        data-testid="lineup-panel"
        className="absolute inset-0 z-40"
      >
        {/* Backdrop: no save-on-click, so a stray tap can't commit edits */}
        <motion.div
          className="absolute inset-0 bg-black/50"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        />
        <motion.div
          className={cn(
            // Size
            "absolute inset-y-0 left-0 w-full max-w-[500px]",
            // Layout
            "flex flex-col",
            // Background
            "bg-tagging-sidebar-bg",
            // Effects
            "shadow-xl",
          )}
          initial={{ x: "-100%" }}
          animate={{ x: 0 }}
          exit={{ x: "-100%" }}
          transition={{ type: "tween", duration: 0.25, ease: "easeOut" }}
        >
          <PanelContent
            data={data}
            rosters={rosters}
            teamDisplays={teamDisplays}
            onSave={onSave}
            onClose={onClose}
          />
        </motion.div>
      </div>
    )}
  </AnimatePresence>
);
